// ⚠️ GENERATED COPY — DO NOT EDIT.
// Source: shared/lib/addressComplete.ts — edit there, then run: bash scripts/sync_shared_code.sh
// CI verifies these copies match the source (sync_shared_code.sh --check).
/**
 * Canada Post AddressComplete client — patient address autocomplete (core).
 *
 * Privacy: each query sends ONLY the typed address fragment to Canada Post
 * (Canadian entity, Canadian processing). No name, DOB, or health data rides
 * along — assessed and approved 2026-07-21. The API key comes from the app's
 * lookups endpoint, bound in the per-app adapter (physician:
 * `/api/config/lookups`; patient: `/api/bridge/config/lookups`) — never
 * hardcoded here — so it rotates without an SPA rebuild; when no key is
 * configured the feature is silently OFF and the address fields stay plain
 * inputs. Key fetching lives in the adapters (via makeLookupKeyFetcher in
 * ./lookupsKey); this core is endpoint-free.
 */

const AC_BASE = "https://ws1.postescanada-canadapost.ca/AddressComplete/Interactive";

export interface AddressSuggestion {
  id: string;
  text: string;
  description: string;
  isRetrievable: boolean;
}

export interface AddressRecord {
  address: string;
  suite: string;
  city: string;
  province: string;
  postal_code: string;
}

/**
 * Find suggestions for a typed fragment. Returns [{id, text, description, isRetrievable}].
 * `lastId` drills into a container result (e.g. a street with many numbers).
 */
export async function findAddresses(key: string, searchTerm: string, lastId = ""): Promise<AddressSuggestion[]> {
  const params = new URLSearchParams({
    Key: key,
    SearchTerm: searchTerm,
    Country: "CAN",
    LanguagePreference: "en",
    MaxResults: "10",
  });
  if (lastId) params.set("LastId", lastId);
  const r = await fetch(`${AC_BASE}/Find/v2.10/json3.ws?${params.toString()}`);
  if (!r.ok) return [];
  const data = await r.json();
  const items = (data && data.Items) || [];
  // An Error item comes back as [{Error: "...", Description: ...}]
  if (items.length && items[0].Error !== undefined) return [];
  return items.map((it: any) => ({
    id: it.Id,
    text: it.Text || "",
    description: it.Description || "",
    isRetrievable: it.Next === "Retrieve",
  }));
}

/**
 * Retrieve the full record for a retrievable suggestion.
 * Returns {address, suite, city, province, postal_code} or null.
 */
export async function retrieveAddress(key: string, id: string): Promise<AddressRecord | null> {
  const params = new URLSearchParams({ Key: key, Id: id });
  const r = await fetch(`${AC_BASE}/Retrieve/v2.11/json3.ws?${params.toString()}`);
  if (!r.ok) return null;
  const data = await r.json();
  const items = (data && data.Items) || [];
  if (!items.length || items[0].Error !== undefined) return null;
  const rec = items.find((it: any) => it.Language === "ENG") || items[0];
  return {
    // Line1 already excludes the sub-building when SubBuilding is populated.
    address: rec.Line1 || "",
    suite: rec.SubBuilding || "",
    city: rec.City || "",
    province: rec.ProvinceCode || rec.Province || "",
    postal_code: rec.PostalCode || "",
  };
}
